import React, { useContext, useMemo } from 'react'
import { DashboardContext } from '../../../context/DashboardContext'
import PropTypes from 'prop-types'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

export function WorkspaceUsage({ className, isCollapsed }) {
  const { activeWorkspace } = useContext(DashboardContext)

  const percentage = useMemo(() => {
    const additional = Number(activeWorkspace?.additional_amount ?? 0)
    const subLeft = Number(activeWorkspace?.sub_hours_left ?? 0)
    const total = Number(activeWorkspace?.total_amount ?? 0)
    if (!Number.isFinite(total) || total <= 0) return 0
    const pct = Math.round(100 - ((additional + subLeft) * 100) / total)
    if (pct < 0) return 0
    if (pct > 100) return 100
    return pct
  }, [activeWorkspace?.additional_amount, activeWorkspace?.sub_hours_left, activeWorkspace?.total_amount])

  // Display remaining time as credits (minutes)
  const creditsLeft = useMemo(() => {
    const additional = Number(activeWorkspace?.additional_amount ?? 0)
    const subLeft = Number(activeWorkspace?.sub_hours_left ?? 0)
    const totalMins = additional + subLeft
    if (!Number.isFinite(totalMins)) return 0
    if (totalMins < 0) return 0
    return Math.round(totalMins)
  }, [activeWorkspace?.additional_amount, activeWorkspace?.sub_hours_left])

  // Convert backend $/hour to $/credit (minute)
  const pricePerCredit = useMemo(() => {
    const perHour = Number(activeWorkspace?.sub_extra_hour_charge ?? 0)
    if (!Number.isFinite(perHour) || perHour <= 0) return null
    return perHour / 60
  }, [activeWorkspace?.sub_extra_hour_charge])

  if (isCollapsed) {
    const radius = 8
    const circumference = 2 * Math.PI * radius
    const pct = Number.isFinite(percentage) ? percentage : 0
    const strokeDashoffset = circumference - (pct / 100) * circumference

    return (
      <TooltipProvider delayDuration={0} skipDelayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`${className} flex justify-center`}>
              <div className="relative w-5 h-5">
                <svg className="w-5 h-5 transform -rotate-90" viewBox="0 0 20 20">
                  {/* Background circle */}
                  <circle
                    cx="10"
                    cy="10"
                    r={radius}
                    stroke="#E6E6E6"
                    strokeWidth="2"
                    fill="none"
                    className="dark:stroke-gray-700"
                  />
                  {/* Progress circle */}
                  <circle
                    cx="10"
                    cy="10"
                    r={radius}
                    stroke="#FF4A8E"
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    className="transition-all duration-300"
                  />
                </svg>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs bg-black text-white border-black">
            <div className="space-y-2">
              <p className="font-semibold">Workspace Usage</p>
              <p>
                {percentage || 0}% used • {creditsLeft || 0} {creditsLeft === 1 ? 'credit' : 'credits'} left
              </p>
              {pricePerCredit ? (
                <p className="text-sm">Overage credits: ${pricePerCredit.toFixed(2)}/credit</p>
              ) : (
                <p className="text-sm">
                  {activeWorkspace.additional_amount
                    ? 'Once your free credits run out, all AI functions will be suspended.'
                    : 'This workspace has run out of free credits. Please upgrade to continue.'}
                </p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <div className={className}>
      <p className="opacity-80 text-slate dark:text-gray-400 text-xs font-extrabold font-['Manrope'] uppercase leading-[13.20px] tracking-wide">
        workspace usage
      </p>
      <div className="mt-[14px] flex items-center justify-between">
        <p className="opacity-80 text-black dark:text-white text-[15px] font-bold font-['Manrope'] leading-[18.75px]">
          {percentage || 0}%
        </p>
        <p className="opacity-80">
          <span className="text-[#40424A] dark:text-gray-300 text-[13px] font-bold font-['Manrope'] leading-none">
            {creditsLeft || 0}
          </span>
          <span className="text-[#40424A] dark:text-gray-300 text-[13px] font-medium font-['Manrope'] leading-none">
            {' '}
            {creditsLeft === 1 ? 'credit' : 'credits'} left
          </span>
        </p>
      </div>
      <div className="mt-[10px] w-full h-[5px] relative bg-[#E6E6E6] dark:bg-gray-700 rounded-[30px]">
        <div
          data-testid="workspace-usage-progress-bar"
          style={{ width: `${percentage}%` }}
          className="h-[5px] left-0 top-0 absolute bg-[#FF4A8E] rounded-[30px]"
        />
      </div>
      {pricePerCredit ? (
        <p className="mt-[14px] opacity-80">
          <span className="text-slate dark:text-gray-400 text-xs font-medium font-['Manrope'] leading-none">
            Under your current plan, overage credits are billed at{' '}
          </span>
          <span className="text-slate dark:text-gray-400 text-xs font-bold font-['Manrope'] leading-none">
            ${pricePerCredit.toFixed(2)}/credit
          </span>
          <span className="text-slate dark:text-gray-400 text-xs font-medium font-['Manrope'] leading-none">.</span>
        </p>
      ) : (
        <p className="mt-[14px] opacity-80">
          <span className="text-slate dark:text-gray-400 text-xs font-medium font-['Manrope'] leading-none">
            {activeWorkspace.additional_amount
              ? 'Once your free credits run out, all AI functions will be suspended.'
              : 'This workspace has run out of free credits. Please upgrade to continue using our AI features.'}
          </span>
        </p>
      )}
      <div className="flex pt-5 justify-center">
        <button
          className="w-full h-8 rounded shadow-inner border border-slate dark:border-gray-600 justify-center items-center gap-3 inline-flex hover:bg-gray-50 dark:hover:bg-gray-700"
          onClick={() => window.open('https://www.evermuse.com/pricing/', '_blank')}
        >
          <div className="text-slate dark:text-gray-300 text-[13px] font-medium font-['Manrope'] leading-none">
            Upgrade
          </div>
        </button>
      </div>
    </div>
  )
}

WorkspaceUsage.propTypes = {
  className: PropTypes.string,
  isCollapsed: PropTypes.bool,
}
